import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  close as closeColorPicker,
  coloris,
  init as initColorPicker,
} from '@melloware/coloris';
import { fromEvent, tap } from 'rxjs';

/* eslint-disable @angular-eslint/directive-class-suffix */
@Component({
  selector: 'color-picker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class ColorPickerComponent
  implements ControlValueAccessor, AfterViewInit, OnInit
{
  @ViewChild('colorInput') colorInput: ElementRef | undefined;
  protected color = '';

  @Input({ required: true }) pickerId!: string;
  @Input() placeholderText!: string;
  @Input() ariaLabelledby!: string;

  protected disabled = false;
  protected onTouch: () => void = () => {};
  protected onChanges: (value: string) => void = () => {};

  private destroyRef = inject(DestroyRef);
  private colorPickerInitialized = false;
  private keydownListenerInitialized = false;
  private isSyncingColorInput = false;

  ngOnInit() {
    if (!this.pickerId) {
      throw new Error('ColorPickerComponent: pickerId is required');
    }
  }

  ngAfterViewInit(): void {
    initColorPicker();
    this.startColorPicker();
  }

  protected startColorPicker(): void {
    if (this.colorPickerInitialized) {
      this.updateColorInputValue();
      return;
    }

    coloris({
      el: `#${this.pickerId}`,
      themeMode: 'auto',
      format: 'rgb',
      formatToggle: false,
      alpha: true,
      focusInput: true,
    });
    this.colorPickerInitialized = true;
    const inputColorValue = document.getElementById('clr-picker');
    if (inputColorValue && !this.keydownListenerInitialized) {
      this.keydownListenerInitialized = true;
      fromEvent<KeyboardEvent>(inputColorValue, 'keydown')
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((event) => event.key === 'Enter' && closeColorPicker()),
        )
        .subscribe();
    }
    this.updateColorInputValue();
  }

  private updateColorInputValue(): void {
    if (this.colorInput) {
      const inputElement = this.colorInput.nativeElement;

      inputElement.value = this.color ?? '';
      inputElement.parentElement?.style.setProperty('color', this.color ?? '');

      this.isSyncingColorInput = true;
      try {
        inputElement.dispatchEvent(new Event('input', { bubbles: true }));
      } finally {
        this.isSyncingColorInput = false;
      }
    }
  }

  protected onColorChange(value: string): void {
    if (this.isSyncingColorInput) {
      return;
    }

    this.onTouch();
    this.onChanges(value);
  }

  writeValue(obj: any): void {
    this.color = obj;
    setTimeout(() => {
      this.startColorPicker();
    });
  }

  registerOnChange(fn: any): void {
    this.onChanges = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
